import PropTypes from 'prop-types';
import { trans } from '@spotahome/soyuz/client';

import { capitalizeFirstLetter } from '@spotahome/ui-library/src/tenant/components/utils/stringUtils';

import { useAvailabilitiesStatisticsContext } from '../../context/AvailabilitiesStatisticsContext';

import {
  generateNoscriptTableHtml,
  getPropertyTypeSections
} from '@spotahome/marketplace-common/src/utils/seoCharts';

import { SECTION_PROPERTY_TYPE_LABEL_MAP } from '@spotahome/marketplace-common/src/constants/seoCharts';

import VerticalBarChart from './VerticalBarChart';

import Table from './Table';
import ChartContainer from './ChartContainer';
import NoDataAvailable from './NoDataAvailable';

const getTranslationKeys = (propertyType, statsLength) => {
  if (propertyType === 'all' || propertyType === null || statsLength === 0) {
    return {
      titleKey: 'chart.availability.title',
      subtitleKey: 'chart.availability.subtitle.plain',
      summaryKey: 'chart.availability.summary.plain'
    };
  }
  return {
    titleKey: 'chart.availability.property.type.title',
    subtitleKey: 'chart.availability.property.type.subtitle.plain',
    summaryKey: 'chart.availability.property.type.summary.plain'
  };
};

const AvailabilitiesChartContainer = ({ location, isMobile = false }) => {
  const {
    availabilitiesStatistics: stats,
    propertyType,
    initialPropertyType,
    setPropertyType,
    average,
    highestMonthStats,
    lowestMonthStats,
    loading
  } = useAvailabilitiesStatisticsContext();

  const sections = getPropertyTypeSections();

  const handleTabChange = tabId => {
    const newPropertyType = sections[tabId].id;
    setPropertyType(newPropertyType);
  };

  const activeIndex = propertyType
    ? sections.findIndex(item => item.id === propertyType)
    : 0;

  const translationsKeys = getTranslationKeys(propertyType, stats.length);
  const propertyTypeTranslation = propertyType
    ? trans(SECTION_PROPERTY_TYPE_LABEL_MAP[propertyType]).toLowerCase()
    : '';

  if (!loading && initialPropertyType && stats.length === 0) {
    return null;
  }

  const renderChart = () => {
    if (!loading && stats.length === 0) {
      return <NoDataAvailable />;
    }

    return (
      <VerticalBarChart
        data={stats}
        loading={loading}
        valueFormatter={value => `${value}%`}
      />
    );
  };

  const renderTable = () => {
    if (!loading && stats.length === 0) {
      return <NoDataAvailable />;
    }

    return (
      <Table
        data={stats}
        valueKey="percentage"
        labelKeys={['chart.table.month', 'chart.table.average.availability']}
        formatValue={value => `${value}%`}
      />
    );
  };

  const title = capitalizeFirstLetter(
    trans(translationsKeys.titleKey, {
      city: location.city,
      propertyType: propertyTypeTranslation
    })
  );

  const chartAriaLabel =
    !loading && stats.length > 0
      ? `${title}: ${stats.map(s => `${s.label} ${s.percentage}%`).join(', ')}`
      : title;

  const noscriptContent =
    stats.length > 0
      ? generateNoscriptTableHtml({
          title,
          headers: [
            trans('chart.table.month'),
            trans('chart.table.average.availability')
          ],
          rows: stats.map(s => [s.label, `${s.percentage}%`, s.delta])
        })
      : '';

  return (
    <ChartContainer
      id="availabilities-chart"
      location={location}
      isMobile={isMobile}
      title={title}
      chartAriaLabel={chartAriaLabel}
      noscriptContent={noscriptContent}
      subtitle={trans(translationsKeys.subtitleKey, {
        city: location.city,
        country: trans(`country.${location.country}`),
        percentage: average,
        propertyType: propertyTypeTranslation
      })}
      summary={trans(translationsKeys.summaryKey, {
        city: location.city,
        highestMonth: highestMonthStats?.monthName,
        lowestMonth: lowestMonthStats?.monthName,
        percentage: lowestMonthStats?.percentage,
        propertyType: propertyTypeTranslation
      })}
      loading={loading}
      activeIndex={activeIndex}
      onTabChange={handleTabChange}
      renderChart={renderChart}
      renderTable={renderTable}
      sections={initialPropertyType ? [] : sections}
    />
  );
};

export default AvailabilitiesChartContainer;

AvailabilitiesChartContainer.propTypes = {
  isMobile: PropTypes.bool,
  location: PropTypes.shape({
    city: PropTypes.string,
    country: PropTypes.string
  }).isRequired
};
